"use client";

import { useSyncExternalStore } from "react";

import type { FaqStory } from "./faqs";
import type { SearchEntry } from "./search";

export const FAQS_DATA_URL = "/data/faqs.json";
export const SEARCH_INDEX_DATA_URL = "/data/search-index.json";

type DataStore<T> = {
  load: () => Promise<T>;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => T | null;
};

function createDataStore<T>(url: string): DataStore<T> {
  let value: T | null = null;
  let pending: Promise<T> | null = null;
  const listeners = new Set<() => void>();

  const load = () => {
    pending ??= fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`${url} ${res.status}`);
        return res.json() as Promise<T>;
      })
      .then((data) => {
        value = data;
        listeners.forEach((listener) => listener());
        return data;
      })
      .catch((error: unknown) => {
        pending = null;
        throw error;
      });
    return pending;
  };

  return {
    load,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => value,
  };
}

export const faqsStore = createDataStore<FaqStory[]>(FAQS_DATA_URL);
export const searchIndexStore = createDataStore<SearchEntry[]>(SEARCH_INDEX_DATA_URL);

export function useDataStore<T>(store: DataStore<T>): T | null {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, () => null);
}

export function preloadSiteData() {
  faqsStore.load().catch(() => {});
  searchIndexStore.load().catch(() => {});
}
